import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SERVICE_OPTIONS = [
  'Mold Inspection',
  'Indoor Air Quality Testing',
  'Infrared Thermal Imaging',
  'Asbestos Testing',
  'Marijuana Grow-Op Inspection',
  'Post-Remediation Clearance',
  'Not sure — need advice',
];

const ContactForm = ({ compact = false }) => {
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', service: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in your name, email and phone.');
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: location.pathname });
      setDone(true);
      toast.success('Request received! We\u2019ll be in touch within one business day.');
      setForm({ name: '', email: '', phone: '', city: '', service: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong. Please call us or try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#eaf4dd] flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-brand" />
        </div>
        <h3 className="mt-5 text-2xl font-bold text-[#13251b]">Thank you!</h3>
        <p className="mt-2 text-[#5b6a60] max-w-sm mx-auto">
          Your request has been received. One of our certified inspectors will contact you within one business day.
        </p>
        <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-full px-6 border-[#cbdcbb] text-[#13251b]">
          Send another request
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={`grid gap-4 ${compact ? '' : 'sm:grid-cols-2'}`}>
        <div className="space-y-1.5">
          <Label htmlFor="name">Full Name *</Label>
          <Input id="name" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Jane Doe" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone *</Label>
          <Input id="phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(226) 000-0000" />
        </div>
      </div>
      <div className={`grid gap-4 ${compact ? '' : 'sm:grid-cols-2'}`}>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="city">City / Town</Label>
          <Input id="city" value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Kitchener" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Service Needed</Label>
        <Select value={form.service} onValueChange={(v) => update('service', v)}>
          <SelectTrigger><SelectValue placeholder="Select a service" /></SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="message">How can we help?</Label>
        <Textarea id="message" rows={compact ? 3 : 4} value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="Tell us about your concern, the property, or any visible signs of mold or moisture..." />
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-brand hover:bg-[#5c9a22] text-white rounded-full font-semibold h-12 text-base">
        {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4 mr-2" /> Request My Quote</>)}
      </Button>
      <p className="text-xs text-muted-foreground text-center">We respond to every request within one business day.</p>
    </form>
  );
};

export default ContactForm;
